<template>
  <div>
    <b-card class="main-card-body" style="background-color: #d6eeff">
      <div class="row">
        <div class="col-sm-1 text-centered" style="margin: auto">
          <i class="fi-checkbox icon-pending" style="font-size: 40px"></i>
        </div>
        <div class="col-sm">
          <p>
            <strong>{{ "Welcome to Pitaya!" }}</strong>
          </p>
          <p>
            {{
              "Let's get your property set up. Complete these steps to start managing your property with Pitaya."
            }}
          </p>
        </div>
        <div
          class="col-sm-2"
          style="display: flex; align-items: center; justify-content: flex-end"
        >
          <b-button @click="finishLater()" variant="outline-secondary"
            >Finish Later</b-button
          >
        </div>
      </div>
    </b-card>

    <b-card
      class="main-card-body"
      v-for="(group, index) in groups"
      :key="index"
      v-if="index !== 'offerings' || hasPOSAccess"
    >
      <template #header class="main">
        <h5 class="card-header-title">{{ group.grouptitle }}</h5>
      </template>
      <b-card-body>
        <div class="container">
          <div v-for="(step, index) in group" :key="index">
            <div v-if="index == 'grouptitle'" style="display: hide"></div>
            <div v-else-if="!step.optional" class="row">
              <div class="col-sm-1" v-if="step.status === true">
                <i class="fi-checkbox icon-completed"></i>
              </div>
              <div class="col-sm-1" v-else-if="step.status === false">
                <i class="fi-checkbox icon-pending"></i>
              </div>
              <div class="col-sm">
                <p>
                  <strong>{{ step.title }}</strong>
                </p>
                <p>{{ step.description }}</p>
              </div>
              <div class="col-sm-2 button-placement">
                <b-button @click="goToUrl(step.slug)" variant="secondary">{{
                  step.buttonCopy
                }}</b-button>
              </div>
            </div>
            <hr v-if="!step.optional" />
          </div>
        </div>
      </b-card-body>
    </b-card>

    <b-card class="main-card-body" style="background-color: #d6eeff">
      <div
        v-if="this.allStepsCompleted == true"
        class="row"
        style="justify-content: center"
      >
        <p>
          <strong>{{ $t("setup.page.completed_note") }} </strong>
        </p>
      </div>
      <div v-else class="row" style="justify-content: center">
        <p>
          <strong>{{ $t("setup.page.incomplete_note") }}</strong>
        </p>
      </div>
      <div class="row" style="justify-content: center; padding-top: 20px">
        <b-button @click="finalizeSetup()" variant="primary" class="mr-2">{{
          $t("setup.page.complete_button")
        }}</b-button>
        <b-button @click="finishLater()" variant="outline-secondary"
          >Finish Later</b-button
        >
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/api";
import apiBase from "@/utils/api-base";
import { mapGetters, mapState, mapActions } from "vuex";
import { MODIFIER_TYPES } from "@/config/constants";

export default {
  name: "Setup",

  async mounted() {
    this.isLoading = true;
    await this.getCurrentVendor();
    await this.getTaxes();
    await this.check_tax_update();
    await this.getDiscountCodes();
    await this.check_stripe();
    await this.check_units_types();
    await this.check_units();
    await this.check_menus();
    await this.check_items();
    this.check_status();
    this.isLoading = false;
  },

  async created() {
    // fetch menus
    this.$store
      .dispatch("getMenus")
      .then(() => {})
      .catch((err) => {
        console.dir(err);
      });

    // Make POS-related tasks required if POS is enabled at component creation
    if (this.hasPOSAccess) {
      this.setPosTaskRequirement();
    }
  },

  computed: {
    ...mapState({
      isRequestingModifiers: (state) => state.modifiers.isRequestingModifiers,
      isRequestingUnits: (state) => state.accommodations.isRequestingUnits,
      isRequestingUnitTypes: (state) =>
        state.accommodations.isRequestingUnitTypes,
      getMenus: (state) => state.menus.menus,
    }),

    ...mapGetters([
      "getAvailableModifiers",
      "getUnitTypesList",
      "getUnits",
      "getAvailableItems",
      "getVendor",
    ]),

    ...mapGetters("subscription", ["hasPOSAccess"]),

    taxesAndFees() {
      // gets and filters modifiers for just taxes and fees
      return this.getAvailableModifiers.filter(
        (modifier) =>
          modifier.category_id === MODIFIER_TYPES.TAX ||
          modifier.category_id === MODIFIER_TYPES.FEE
      );
    },
  },

  methods: {
    ...mapActions([
      "getTaxes", // is there another way to pull this.
    ]),

    getCurrentVendor() {
      // Retreives vendor information
      apiBase
        .get(`/vendor/current`)
        .then((res) => {
          this.formData = res.data ? res.data : {};
        })
        .finally(() => {
          this.check_profile();
        });
    },

    check_profile() {
      // checks if vendor info has been altered from create state
      // This isn't great but it works for the moment. Should be updated to be more specific
      let created = new Date(this.formData.created_at);
      created.setSeconds(0, 0);
      created = created.toISOString();

      let updated = new Date(this.formData.updated_at);
      updated.setSeconds(0, 0);
      updated = updated.toISOString();

      if (created != updated) {
        this.groups.company.profile.status = true;
      }
    },

    check_units_types() {
      // Checks if new unit types have been created
      if (this.getUnitTypesList.length > 0) {
        this.groups.rooms.unit_categories.status = true;
      }
    },

    check_units() {
      // checks if new units have been created
      if (this.getUnits.length > 0) {
        this.groups.rooms.units.status = true;
      }
    },

    check_tax_update() {
      // Check 3 conditions, new rule created or either of the existing base rules updated
      if (this.taxesAndFees.length > 2) {
        this.groups.company.taxes_fees.status = true;
      } else if (this.taxesAndFees[0]["default_rate"] > "0.000000") {
        this.groups.company.taxes_fees.status = true;
      } else if (this.taxesAndFees[1]["default_rate"] > "0.000000") {
        this.groups.company.taxes_fees.status = true;
      }
    },

    check_menus() {
      if (!this.hasPOSAccess) return;
      if (this.getMenus.length > 0) {
        this.groups.offerings.offering_menus.status = true;
      }
    },

    check_items() {
      if (!this.hasPOSAccess) return;
      if (this.getAvailableItems.length > 0) {
        this.groups.offerings.offering_items.status = true;
      }
    },

    check_stripe() {
      // Checks if strip integration as been established
      if (this.getVendor.stripe_location_id != null) {
        this.groups.company.integrations.status = true;
      }
    },

    goToUrl(destination) {
      // directs to other location in pitaya app
      this.$router.push(destination);
    },

    getDiscountCodes() {
      // Fetches the discount codes
      axios
        .get(`${API.BASE_URL}/discountcodes`, API.RequestHeaders())
        .then((res) => {
          let discountCodes = res.data;
          if (discountCodes.length > 0) {
            this.groups.company.discount_codes.status = true;
          }
        });
    },

    completedRedirect() {
      this.$router.push("../dashboard").then(window.location.reload());
    },

    finalizeSetup() {
      // 1 is incompleted, 2 is completed, 3 is skipped
      if (this.allStepsCompleted == false) {
        // steps incomplete recorded as skipped
        this.formData.completed_setup = 3;
      } else {
        // steps complete
        this.formData.completed_setup = 2;
      }

      let data = Object.assign({}, this.formData);
      // Remove unwanted data from object before put requst
      const validKeys = ["completed_setup"];
      Object.keys(data).forEach(
        (key) => validKeys.includes(key) || delete data[key]
      );

      apiBase
        .put(`/vendor/${this.formData.id}`, data)
        .then((response) => {
          this.formData = response.data.original;
        })
        .catch((error) => {
          this.errored = true;
        })
        .finally(() => {
          this.completedRedirect();
        });
    },

    check_status() {
      let group_count = 0;
      let tasks_result = [];

      Object.entries(this.groups).forEach((item) => {
        let section = Object.values(this.groups)[group_count];
        let task_count = 0;

        Object.entries(section).forEach((item) => {
          let task = Object.values(section)[task_count];
          // Only count non-optional tasks for completion
          if (task.status == true && !task.optional) {
            tasks_result.push(task.status);
          }
          task_count++;
        });
        group_count++;
      });

      let item_count = 4; // total required items
      let non_title_length = tasks_result.length; // just count the completed tasks

      if (item_count == non_title_length) {
        this.allStepsCompleted = true;
      }
    },

    finishLater() {
      // Set setup as skipped
      this.formData.completed_setup = 3;

      let data = Object.assign({}, this.formData);
      // Remove unwanted data from object before put request
      const validKeys = ["completed_setup"];
      Object.keys(data).forEach(
        (key) => validKeys.includes(key) || delete data[key]
      );

      apiBase
        .put(`/vendor/${this.formData.id}`, data)
        .then((response) => {
          this.formData = response.data.original;
        })
        .catch((error) => {
          this.errored = true;
        })
        .finally(() => {
          this.completedRedirect();
        });
    },

    // central helper to flip optional flag
    setPosTaskRequirement() {
      this.groups.offerings.offering_menus.optional = false;
      this.groups.offerings.offering_items.optional = false;
    },
  },

  data() {
    return {
      filter: "",
      allStepsCompleted: false,
      formData: {}, // vendor form data
      errored: false,
      groups: {
        company: {
          grouptitle: this.$t("setup.page.company.group_title"),
          profile: {
            title: this.$t("setup.page.company.profile_title"),
            description: this.$t("setup.page.company.profile_description"),
            status: false,
            slug: "settings/information",
            buttonCopy: this.$t("setup.page.company.profile_button"),
            optional: false,
          },
          taxes_fees: {
            title: this.$t("setup.page.company.taxes_title"),
            description: this.$t("setup.page.company.taxes_description"),
            status: false,
            slug: "settings/taxes",
            buttonCopy: this.$t("setup.page.company.taxes_button"),
            optional: false,
          },
          discount_codes: {
            title: this.$t("setup.page.company.discount_title"),
            description: this.$t("setup.page.company.discount_description"),
            status: false,
            slug: "settings/discount-codes",
            buttonCopy: this.$t("setup.page.company.discount_button"),
            optional: true,
          },
          integrations: {
            title: this.$t("setup.page.company.integration_title"),
            description: this.$t("setup.page.company.integration_description"),
            status: false,
            slug: "settings/integrations",
            buttonCopy: this.$t("setup.page.company.integration_button"),
            optional: true,
          },
        },

        rooms: {
          grouptitle: this.$t("setup.page.rooms.group_title"),
          unit_categories: {
            title: this.$t("setup.page.rooms.categories_title"),
            description: this.$t("setup.page.rooms.categories_description"),
            status: false,
            slug: "settings/accommodations",
            buttonCopy: this.$t("setup.page.rooms.categories_button"),
            optional: false,
          },
          units: {
            title: this.$t("setup.page.rooms.units_title"),
            description: this.$t("setup.page.rooms.units_description"),
            status: false,
            slug: "settings/accommodations",
            buttonCopy: this.$t("setup.page.rooms.units_button"),
            optional: false,
          },
        },

        offerings: {
          grouptitle: this.$t("setup.page.offerings.group_title"),
          offering_menus: {
            title: this.$t("setup.page.offerings.categories_title"),
            description: this.$t("setup.page.offerings.categories_description"),
            status: false,
            slug: "settings/menucategories",
            buttonCopy: this.$t("setup.page.offerings.categories_button"),
            optional: true,
          },
          offering_items: {
            title: this.$t("setup.page.offerings.offering_title"),
            description: this.$t("setup.page.offerings.offering_description"),
            status: false,
            slug: "settings/inventory",
            buttonCopy: this.$t("setup.page.offerings.offering_button"),
            optional: true,
          },
        },
      },
    };
  },

  watch: {
    hasPOSAccess: {
      handler(val) {
        if (val) {
          this.setPosTaskRequirement();
        }
      },
      immediate: true,
    },
  },
};
</script>

<style>
.avatar_image {
  width: 100%;
  max-width: 40px;
  border-radius: 50%;
}

p {
  margin-bottom: 0;
}

.icon-completed {
  /* task icon color  */
  color: green;
  font-size: 30px;
}

.icon-pending {
  /* task icon color  */
  color: gray;
  font-size: 30px;
}

.card-header {
  background-color: white;
}

.card-header-title {
  margin-top: 1.5em;
  margin-bottom: 1.5em;
}

.main-card-body {
  /* provides a large white spacing against the sides */
  margin-top: 2em;
  margin-left: 10vw;
  margin-right: 10vw;
}

.button-placement {
  /* keeps buttons positioned to right of column */
  display: flex;
  justify-content: right;
}
</style>
